import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATS = [
  { value: "65+", label: "login.stats.buildings" },
  { value: "2", label: "login.stats.brands" },
  { value: "24/7", label: "login.stats.control" },
];

export default function LoginPage() {
  const { t } = useT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiError(err, t("login.error")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-paper">
      <div className="relative hidden w-[46%] flex-col justify-between overflow-hidden bg-ink px-12 py-12 lg:flex">
        <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-tertiary">
          {t("login.panel_tag")}
        </p>

        <div>
          <h1 className="text-[44px] font-semibold leading-[1.05] tracking-[-0.02em] text-paper">
            Beto's<br />Handyman
          </h1>
          <p className="mt-4 max-w-[340px] text-[15px] leading-relaxed text-ink-tertiary">
            {t("login.panel_desc")}
          </p>
        </div>

        <div className="flex divide-x divide-white/10 border-t border-white/10 pt-6">
          {STATS.map((s) => (
            <div key={s.label} className="flex-1 px-6 first:px-0 first:pr-6">
              <p className="font-mono text-[28px] font-medium tracking-[-0.02em] text-paper">{s.value}</p>
              <p className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-tertiary">
                {t(s.label)}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-[360px]">
          <div className="mb-8 lg:hidden">
            <p className="text-[20px] font-semibold text-ink">Beto's Handyman</p>
            <p className="mt-0.5 text-[13px] text-ink-secondary">{t("login.subtitle")}</p>
          </div>

          <h2 className="text-[20px] font-semibold tracking-[-0.02em] text-ink">{t("login.title")}</h2>
          <p className="mt-1 text-[13px] text-ink-secondary">{t("login.desc")}</p>

          <form onSubmit={submit} data-testid="login-form" className="mt-7 border-t border-line pt-6">
            <div className="mb-4">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">
                {t("login.email")}
              </Label>
              <Input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-10 text-[14px]"
                autoComplete="email"
                autoFocus
              />
            </div>
            <div className="mb-6">
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">
                {t("login.password")}
              </Label>
              <Input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10 text-[14px]"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <p
                data-testid="login-error"
                className="mb-5 border-l-2 border-state-critical bg-state-critical-soft px-3 py-2 text-[12.5px] text-state-critical"
              >
                {error}
              </p>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="h-10 w-full text-[13.5px] font-medium"
            >
              {loading ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
              )}
              {loading ? t("login.submitting") : t("login.submit")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
